import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCatalogSummary } from '../hooks/useCatalogSummary';
import { consumerGuideUrl } from '../lib/consumerGuideLink';

interface LinkCard {
  key: 'showcase' | 'standards' | 'terminology' | 'faq';
  to: string;
  label: string;
  description: string;
  showBadge: boolean;
}

// #660 §UAT-2026-07-09-R660 §R660-4 — FAQ 카드는 consumer-guide 인앱 뷰어의 FAQ 섹션
// 앵커(`#faq`)로 이동한다. rehypeSlug 산출 id 가 불안정하면 ConsumerGuidePage 가 텍스트
// 매칭 폴백으로 스크롤한다(§R660-4) — 여기서는 링크 대상만 구성한다.
const FAQ_LINK = `${consumerGuideUrl()}#faq`;

/**
 * §6.6 섹션 4 — 딥링크 카드(능력 카탈로그/개발표준/용어/자주 걸리는 문제).
 * 능력 카탈로그 카드만 (선택) 기존 GET /api/eraf/biz/catalog 재사용으로
 * "완료 공통 코드 수" 뱃지를 표시한다. 로딩/에러는 뱃지 국소 범위(§6.8).
 * #660: 4번째 카드 "자주 걸리는 문제"가 consumer-guide 인앱 뷰어의 FAQ 앵커로 딥링크한다.
 */
export function RelatedLinkCards() {
  const { t } = useTranslation();
  const { completedCount, isLoading } = useCatalogSummary();

  const cards: LinkCard[] = [
    {
      key: 'showcase',
      to: '/showcase',
      label: t('gettingStartedGuide.links.showcase'),
      description: t('gettingStartedGuide.links.showcaseDesc'),
      showBadge: true,
    },
    {
      key: 'standards',
      to: '/standards',
      label: t('gettingStartedGuide.links.standards'),
      description: t('gettingStartedGuide.links.standardsDesc'),
      showBadge: false,
    },
    {
      key: 'terminology',
      to: '/admin/terminology',
      label: t('gettingStartedGuide.links.terminology'),
      description: t('gettingStartedGuide.links.terminologyDesc'),
      showBadge: false,
    },
    {
      key: 'faq',
      to: FAQ_LINK,
      label: t('gettingStartedGuide.links.faq'),
      description: t('gettingStartedGuide.links.faqDesc'),
      showBadge: false,
    },
  ];

  return (
    <section aria-labelledby="gsg-links-heading">
      <h2
        id="gsg-links-heading"
        style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.links.heading')}
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--spacing-4)',
        }}
      >
        {cards.map((card) => (
          <Link
            key={card.key}
            to={card.to}
            aria-label={`${card.label} — ${card.description}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-1)',
              padding: 'var(--spacing-4)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              color: 'var(--color-text-primary)',
              background: 'var(--color-surface-background)',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>{card.label}</span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              {card.description}
            </span>
            {card.showBadge && (
              isLoading ? (
                <span
                  role="status"
                  aria-label={t('gettingStartedGuide.links.showcaseBadgeLoading')}
                  style={{
                    marginTop: 'var(--spacing-1)',
                    width: '120px',
                    height: '14px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-muted)',
                    animation: 'gsg-pulse 1.5s ease-in-out infinite',
                  }}
                />
              ) : completedCount != null ? (
                <span
                  style={{
                    marginTop: 'var(--spacing-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-success)',
                  }}
                >
                  {t('gettingStartedGuide.links.showcaseBadge', { count: completedCount })}
                </span>
              ) : null
            )}
          </Link>
        ))}
      </div>
      <style>{`
        @keyframes gsg-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </section>
  );
}
